import { useMutation, useQuery } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Modal } from "@/componentes/ui/Modal"
import { FECHA_MAXIMA, FECHA_MINIMA, fechaFueraDeRango } from "@/lib/fechas"

/**
 * El formulario del borrador.
 *
 * ⚠ **La fecha de fin aparece según el tipo, y eso replica lo que el servidor
 * exige** — no una regla más estricta inventada aquí. Un indeterminado con fecha de
 * fin no es indeterminado: el servidor lo rechaza, así que el campo ni se ofrece. Un
 * determinado sin ella también, así que se pide obligatoria. Para pasantía y
 * honorarios el servidor admite las dos cosas, y por eso el campo está y es
 * opcional. Ofrecer un formulario que va a responder 409 es peor que no ofrecerlo.
 */

type Tipo = "DETERMINADO" | "INDETERMINADO" | "PASANTIA" | "HONORARIOS"

const TIPOS: [Tipo, string][] = [
  ["DETERMINADO", "A tiempo determinado"],
  ["INDETERMINADO", "A tiempo indeterminado"],
  ["PASANTIA", "Pasantía"],
  ["HONORARIOS", "Honorarios profesionales"],
]

const VACIO = { cargo_id: "", tipo: "DETERMINADO" as Tipo, inicio: "", fin: "", salario: "" }

export function NuevoContrato({
  personaId,
  cargoId,
  abierto,
  onCerrar,
  onCreado,
}: {
  personaId: number
  /** El cargo de la ficha, como valor de partida. Se puede cambiar. */
  cargoId: number | null
  abierto: boolean
  onCerrar: () => void
  onCreado: () => void
}) {
  const [datos, setDatos] = useState({ ...VACIO, cargo_id: cargoId ? String(cargoId) : "" })

  const cargos = useQuery({
    queryKey: ["cargos", "activos"],
    queryFn: () => api.get<{ id: number; nombre: string }[]>("/api/v1/cargos?solo_activos=true"),
    enabled: abierto,
  })

  const llevaFin = datos.tipo !== "INDETERMINADO"
  const finObligatorio = datos.tipo === "DETERMINADO"

  const crear = useMutation({
    mutationFn: () =>
      api.post("/api/v1/contratos", {
        persona_id: personaId,
        cargo_id: Number(datos.cargo_id),
        tipo: datos.tipo,
        inicio: datos.inicio,
        // `null` y no cadena vacía: sin fecha de fin la columna es NULL, que es lo
        // que hace que el recordatorio de vencimiento no avise nunca de un
        // indefinido.
        fin: llevaFin && datos.fin !== "" ? datos.fin : null,
        salario: datos.salario === "" ? null : datos.salario,
        divisa: datos.salario === "" ? null : "USD",
      }),
    onSuccess: () => {
      setDatos({ ...VACIO, cargo_id: cargoId ? String(cargoId) : "" })
      onCreado()
    },
  })

  const fechasMal =
    fechaFueraDeRango(datos.inicio) ||
    fechaFueraDeRango(datos.fin) ||
    (datos.fin !== "" && datos.inicio !== "" && datos.fin < datos.inicio)

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (!fechasMal) crear.mutate()
  }

  return (
    <Modal
      abierto={abierto}
      titulo="Nuevo contrato"
      descripcion="Nace en borrador: su texto se lee en vivo hasta que lo emitas."
      onCerrar={onCerrar}
    >
      <form onSubmit={enviar} aria-label="Nuevo contrato">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="c-cargo" className="mb-1 block text-xs font-medium uppercase">
              Cargo
            </label>
            <select
              id="c-cargo"
              required
              className="campo"
              value={datos.cargo_id}
              onChange={(e) => setDatos({ ...datos, cargo_id: e.target.value })}
            >
              <option value="">Elige uno…</option>
              {(cargos.data ?? []).map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="c-tipo" className="mb-1 block text-xs font-medium uppercase">
              Tipo
            </label>
            <select
              id="c-tipo"
              className="campo"
              value={datos.tipo}
              /* Al pasar a indeterminado se limpia el fin: si no, quedaría escrito
                 en un campo que ya no se ve y viajaría al servidor. */
              onChange={(e) => setDatos({ ...datos, tipo: e.target.value as Tipo, fin: "" })}
            >
              {TIPOS.map(([v, t]) => (
                <option key={v} value={v}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="c-inicio" className="mb-1 block text-xs font-medium uppercase">
              Inicio
            </label>
            <input
              id="c-inicio"
              type="date"
              required
              min={FECHA_MINIMA}
              max={FECHA_MAXIMA}
              className="campo tabular-nums"
              value={datos.inicio}
              onChange={(e) => setDatos({ ...datos, inicio: e.target.value })}
            />
          </div>

          {llevaFin && (
            <div>
              <label htmlFor="c-fin" className="mb-1 block text-xs font-medium uppercase">
                Fin {finObligatorio ? "" : "(opcional)"}
              </label>
              <input
                id="c-fin"
                type="date"
                required={finObligatorio}
                min={datos.inicio === "" ? FECHA_MINIMA : datos.inicio}
                max={FECHA_MAXIMA}
                className="campo tabular-nums"
                value={datos.fin}
                onChange={(e) => setDatos({ ...datos, fin: e.target.value })}
              />
            </div>
          )}

          <div>
            <label htmlFor="c-salario" className="mb-1 block text-xs font-medium uppercase">
              Salario pactado (USD)
            </label>
            <input
              id="c-salario"
              type="number"
              min="0"
              step="0.01"
              className="campo tabular-nums"
              value={datos.salario}
              onChange={(e) => setDatos({ ...datos, salario: e.target.value })}
            />
          </div>
        </div>

        {fechasMal && (
          <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
            Revisa las fechas: el contrato no puede terminar antes de empezar.
          </p>
        )}

        {crear.error && (
          <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
            {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo crear"}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className="boton-secundario h-9 px-3 text-sm">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={crear.isPending || fechasMal}
            data-testid="crear-contrato"
            className="boton-primario h-9 px-3 text-sm"
          >
            {crear.isPending ? "Creando…" : "Crear borrador"}
          </button>
        </div>
      </form>
    </Modal>
  )
}
